import { tx as translateInterfaceText } from '@/utils/i18n';
import { useLanguage } from '@/hooks/useLanguage';
import React, { useRef, useEffect, useState } from 'react';
import { Label } from '@/components/ui/label';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { 
  Type, 
  Bold, 
  Italic, 
  Underline, 
  List, 
  ListOrdered, 
  AlignLeft, 
  AlignCenter,
  Link2
} from 'lucide-react';
import { cn } from '@/lib/utils';
import { validateUrl } from '@/lib/security';

interface VideoDescriptionEditorProps {
  title: string;
  description: string;
  ctaButton?: {
    enabled: boolean;
    text: string;
    url: string;
  };
  onTitleChange: (title: string) => void;
  onDescriptionChange: (description: string) => void;
  onCtaButtonChange: (cta: { enabled: boolean; text: string; url: string }) => void;
}

export const VideoDescriptionEditor: React.FC<VideoDescriptionEditorProps> = ({
  title,
  description,
  ctaButton,
  onTitleChange,
  onDescriptionChange,
  onCtaButtonChange,
}) => {
  useLanguage();
  const editorRef = useRef<HTMLDivElement>(null);
  const [urlError, setUrlError] = useState<string | null>(null);

  const getUrlErrorMessage = (error: string): string => {
    const messages: Record<string, string> = {
      'URL must be a non-empty string': translateInterfaceText("URL não pode estar vazia"),
      'URL too long': translateInterfaceText("URL muito longa (máximo 2000 caracteres)"),
      'Invalid URL protocol': translateInterfaceText("Protocolo inválido. Use http://, https://, mailto: ou tel:"),
      'URL contains suspicious patterns': translateInterfaceText("URL contém padrões suspeitos"),
      'Invalid URL format': translateInterfaceText("Formato de URL inválido. Ex: https://exemplo.com"),
    };
    return messages[error] || translateInterfaceText("URL inválida");
  };

  const handleUrlChange = (url: string) => {
    // Se vazio, limpar erro e permitir
    if (!url.trim()) {
      setUrlError(null);
      onCtaButtonChange({
        enabled: ctaButton?.enabled || false,
        text: ctaButton?.text || '',
        url: '',
      });
      return;
    }
    
    // Validar URL
    const validation = validateUrl(url);
    
    if (!validation.isValid) {
      setUrlError(getUrlErrorMessage(validation.error || 'Invalid URL format'));
    } else {
      setUrlError(null);
    }
    
    // Sempre atualizar o valor (para o usuário poder corrigir)
    onCtaButtonChange({
      enabled: ctaButton?.enabled || false,
      text: ctaButton?.text || '',
      url: url,
    });
  };

  useEffect(() => {
    if (editorRef.current && !editorRef.current.innerHTML) {
      editorRef.current.innerHTML = description || '';
    }
  }, []);

  const execCommand = (command: string, value?: string) => {
    document.execCommand(command, false, value);
    editorRef.current?.focus();
  };

  const handleHeading = (level: number) => {
    execCommand('formatBlock', `h${level}`);
  };

  const handleLink = () => {
    const url = prompt('Insira a URL:');
    if (url) {
      execCommand('createLink', url);
    }
  };

  const handleInput = () => {
    if (editorRef.current) {
      onDescriptionChange(editorRef.current.innerHTML);
    }
  };

  const toolbarButtons = [
    {
      group: 'heading',
      buttons: [
        { icon: Type, label: 'H1', action: () => handleHeading(1), size: 'w-4 h-4' },
        { icon: Type, label: 'H2', action: () => handleHeading(2), size: 'w-3.5 h-3.5' },
        { icon: Type, label: 'H3', action: () => handleHeading(3), size: 'w-3 h-3' },
      ],
    },
    {
      group: 'format',
      buttons: [
        { icon: Bold, label: translateInterfaceText("Negrito"), action: () => execCommand('bold') },
        { icon: Italic, label: translateInterfaceText("Itálico"), action: () => execCommand('italic') },
        { icon: Underline, label: translateInterfaceText("Sublinhado"), action: () => execCommand('underline') },
      ],
    },
    {
      group: 'list',
      buttons: [
        { icon: List, label: translateInterfaceText("Lista"), action: () => execCommand('insertUnorderedList') },
        { icon: ListOrdered, label: translateInterfaceText("Numeração"), action: () => execCommand('insertOrderedList') },
      ],
    },
    {
      group: 'align',
      buttons: [
        { icon: AlignLeft, label: translateInterfaceText("Esquerda"), action: () => execCommand('justifyLeft') },
        { icon: AlignCenter, label: translateInterfaceText("Centro"), action: () => execCommand('justifyCenter') },
      ],
    },
    {
      group: 'link',
      buttons: [
        { icon: Link2, label: translateInterfaceText("Link"), action: handleLink },
      ],
    },
  ];

  return (
    <div className="space-y-4">
      {/* Título do episódio */}
      <div>
        <Label className="text-sm font-medium text-[#111]">
          {translateInterfaceText("Título do Episódio")}</Label>
        <Input
          value={title}
          onChange={(e) => onTitleChange(e.target.value.slice(0, 90))}
          placeholder={translateInterfaceText("Digite o título do vídeo")}
          maxLength={90}
          className="mt-1.5"
        />
        <p className="text-xs text-muted-foreground mt-1">
          {(title || '').length}/90 caracteres
        </p>
      </div>

      {/* Descrição completa */}
      <div>
        <Label className="text-sm font-medium text-[#111]">
          {translateInterfaceText("Descrição do Episódio")}</Label>
        
        {/* Toolbar */}
        <div className="mt-1.5 border border-[#E5E7EB] rounded-t-lg bg-[#F9FAFB] p-2 flex items-center gap-1 flex-wrap">
          {toolbarButtons.map((group, groupIndex) => (
            <React.Fragment key={group.group}>
              {groupIndex > 0 && (
                <div className="w-px h-6 bg-[#E5E7EB] mx-1" />
              )}
              {group.buttons.map((button, btnIndex) => {
                const Icon = button.icon;
                return (
                  <Button
                    key={btnIndex}
                    type="button"
                    variant="ghost"
                    size="sm"
                    onClick={button.action}
                    className={cn(
                      "h-8 w-8 p-0 hover:bg-[#3B82F6]/10 hover:text-[#3B82F6]",
                      button.label.startsWith('H') && "relative"
                    )}
                    title={button.label}
                  >
                    <Icon className={button.size || "w-4 h-4"} />
                    {button.label.startsWith('H') && (
                      <span className="absolute bottom-0.5 right-0.5 text-[9px] font-bold">
                        {button.label}
                      </span>
                    )}
                  </Button>
                );
              })}
            </React.Fragment>
          ))}
        </div>

        {/* Editor contentEditable */}
        <div
          ref={editorRef}
          contentEditable
          onInput={handleInput}
          className="min-h-[200px] p-4 border border-t-0 border-[#E5E7EB] rounded-b-lg bg-white focus:outline-none focus:ring-2 focus:ring-[#3B82F6] focus:ring-offset-0 prose prose-sm max-w-none"
          style={{
            wordBreak: 'break-word',
            overflowWrap: 'break-word',
          }}
        />
      </div>

      {/* Botão de Ação (CTA) */}
      <div className="border border-[#E5E7EB] rounded-lg p-4 bg-[#FAFBFC]">
        <div className="flex items-center gap-2 mb-3">
          <Link2 className="w-4 h-4 text-[#3B82F6]" />
          <Label className="text-sm font-semibold text-[#111]">
            {translateInterfaceText("Botão de Ação (CTA)")}</Label>
        </div>

        {/* Toggle */}
        <div className="flex items-center justify-between mb-4 p-3 bg-white rounded-lg border border-[#E5E7EB]">
          <span className="text-sm text-[#374151]">{translateInterfaceText("Exibir botão de CTA abaixo do vídeo")}</span>
          <input
            type="checkbox"
            checked={ctaButton?.enabled || false}
            onChange={(e) => onCtaButtonChange({
              enabled: e.target.checked,
              text: ctaButton?.text || '',
              url: ctaButton?.url || '',
            })}
            className="w-10 h-6 rounded-full appearance-none cursor-pointer transition-colors
                       bg-[#D1D5DB] checked:bg-[#3B82F6] relative
                       before:content-[''] before:absolute before:w-4 before:h-4 before:rounded-full 
                       before:bg-white before:top-1 before:left-1 before:transition-transform
                       checked:before:translate-x-4"
          />
        </div>

        {/* Campos - aparecem quando enabled */}
        {ctaButton?.enabled && (
          <div className="space-y-3 animate-in fade-in-0 slide-in-from-top-2">
            {/* Texto do Botão */}
            <div>
              <div className="flex items-center justify-between mb-1.5">
                <Label className="text-xs font-medium text-[#374151]">{translateInterfaceText("Texto do Botão")}</Label>
                <span className="text-xs text-[#6B7280]">
                  {(ctaButton?.text || '').length}/20
                </span>
              </div>
              <Input
                value={ctaButton?.text || ''}
                onChange={(e) => onCtaButtonChange({
                  enabled: ctaButton?.enabled || false,
                  text: e.target.value.slice(0, 20),
                  url: ctaButton?.url || '',
                })}
                placeholder={translateInterfaceText("Ex: Comprar Agora")}
                maxLength={20}
                className="h-10"
              />
            </div>

            {/* URL de Destino */}
            <div>
              <Label className="text-xs font-medium text-[#374151] mb-1.5 block">
                {translateInterfaceText("URL de Destino")}</Label>
              <Input
                value={ctaButton?.url || ''}
                onChange={(e) => handleUrlChange(e.target.value)}
                placeholder="https://exemplo.com/oferta"
                className={cn(
                  "h-10",
                  urlError && "border-red-500 focus-visible:ring-red-500"
                )}
              />
              {/* Mensagem de erro */}
              {urlError && (
                <p className="text-xs text-red-500 mt-1 flex items-center gap-1">
                  <svg className="w-3 h-3" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                  </svg>
                  {urlError}
                </p>
              )}
              {/* Confirmação de URL válida */}
              {!urlError && ctaButton?.url && (
                <p className="text-xs text-green-600 mt-1 flex items-center gap-1">
                  <svg className="w-3 h-3" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                  </svg>
                  {translateInterfaceText("URL válida")}</p>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};